import React, { useMemo } from 'react'
import cn from 'classnames'
import type { Answer } from 'types/answers'
import QuoteLine from 'components/answers/QuoteLine'
import { quotePreviewForAnswer } from 'components/answers/quotePreview'
import usePanelContext from 'refactor/hooks/usePanelContext'
import styles from './composer-auto-quote.module.scss'

interface ComposerAutoQuoteProps {
    answer?: Partial<Answer> | null
    onRemove: () => void
    // Bumped each time the reader asks for this quote. The key restarts the
    // animation — the same element re-running a CSS animation needs a remount,
    // and the pill is presentational, so there is nothing to lose by remounting.
    attentionKey?: number
    // 'enter' when the pill was not there a moment ago, 'pulse' when it was and
    // the press is pointing it out again.
    attentionKind?: string
    // A control the composer keeps at its top-right corner, right of the X (the
    // bottom editor's door to the full editor).
    trailing?: React.ReactNode
}

// The inline composer's requested quote, in the one-line grammar the sent reply
// will wear — the same QuoteLine a posted answer shows, not the modal's opened
// block. It sits under the byline, so it has to read as one line of chrome
// rather than a second post. The X sits beside the pill at the right margin,
// outside it: the pill hugs its quote the way a sent one does, and the X is
// its own control rather than a part of the quote. The quote was asked for
// with a press on the message above, so refusing it stays as cheap as asking.
const ComposerAutoQuote: React.FC<ComposerAutoQuoteProps> = ({
    answer,
    onRemove,
    attentionKey = 0,
    attentionKind = 'enter',
    trailing,
}) => {
    const preview = useMemo(() => quotePreviewForAnswer(answer), [answer])
    const unavailable = !!(answer?.deleted || answer?.archive)
    const { panel } = usePanelContext()

    // A quote is a way back to what it quotes, here as much as in a sent
    // message — and it is what earns the row its hover and press states, which
    // QuoteLine gives only to a line that actually goes somewhere. The target
    // is this thread's own trailing message, so a reveal in place is the whole
    // trip; no panel retargeting is needed to find it.
    const onReveal =
        answer?.id && !unavailable && panel?.requestAnswerReveal
            ? () => {
                  void panel.requestAnswerReveal?.(String(answer.id), {
                      questId: panel.filter?.questId,
                      highlight: true,
                  })
              }
            : undefined

    return (
        <div className={styles.autoQuote} data-testid="composer-auto-quote">
            <span className={styles.screenReaderLabel}>Replying to</span>
            <QuoteLine
                key={attentionKey}
                authorName={answer?.user?.name}
                preview={preview}
                unavailable={unavailable}
                className={cn(
                    attentionKey > 0 &&
                        (attentionKind === 'pulse' ? styles.attentionPulse : styles.attentionEnter),
                )}
                onClick={onReveal}
            />
            <button
                type="button"
                className={styles.remove}
                data-testid="composer-auto-quote-remove"
                aria-label="Don't quote this message"
                // Removing the quote is part of writing: the editor keeps its
                // focus (and a phone its keyboard) through the press.
                onMouseDown={ev => ev.preventDefault()}
                onClick={ev => {
                    ev.preventDefault()
                    ev.stopPropagation()
                    onRemove()
                }}
            >
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    aria-hidden="true"
                >
                    <path d="M18 6 6 18M6 6l12 12" />
                </svg>
                {/* The key that does the same thing, named on the control
                        that does it — the only place a reader would look for it.
                        Desktop only: there is no Esc key to press on a phone. */}
                <span className={styles.shortcut} aria-hidden="true">
                    Esc
                </span>
            </button>
            {trailing}
        </div>
    )
}

export default ComposerAutoQuote
